import { type ReactNode, useState } from 'react'
import { isAxiosError } from 'axios'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, Bot, ChevronDown, ChevronRight, FileText, RefreshCw } from 'lucide-react'
import {
  Button,
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownTrigger,
  ErrorState,
  IconButton,
  LoadingState,
  Modal,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useToast,
} from '@/components/ui'
import { SapDocumentView } from '@/components/sap/SapDocumentView.tsx'
import { AngebotDialogPage } from '@/pages/auftraege/AngebotDialogPage.tsx'
import {
  getDocumentDetail,
  isDocumentOpen,
  formatDocNum,
  triggerSync,
  ALLOWED_CONVERSIONS,
  DOC_TYPE_LABELS,
  type CachedDocument,
  type DocType,
} from '@/api/sapDocuments.ts'

interface Props {
  doc: CachedDocument | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onConvert: (doc: CachedDocument, targetType: DocType) => void
  onDeleted?: () => void
  /** Optional seiten-spezifische Aktionen, die in der Action-Bar gerendert werden */
  actions?: ReactNode
}

function extractErrorMessage(err: unknown): { description: string; details?: string } {
  if (isAxiosError(err)) {
    const backendMsg = (err.response?.data as { error?: string })?.error
    if (backendMsg) return { description: backendMsg, details: `HTTP ${err.response?.status} – ${err.message}` }
    return { description: err.message }
  }
  return { description: err instanceof Error ? err.message : 'Unbekannter Fehler' }
}

export function DocumentDetailModal({ doc, open, onOpenChange, onConvert, onDeleted, actions }: Props) {
  const [refreshKey, setRefreshKey] = useState(0)
  const [isSyncing, setIsSyncing] = useState(false)
  const [outOfSyncDetailsOpen, setOutOfSyncDetailsOpen] = useState(false)
  const { toast } = useToast()
  const isAiDraft = doc?.source === 'ai-draft'
  const isAiExported = doc?.source === 'ai-exported'
  const isAi = isAiDraft || isAiExported
  const [aiTab, setAiTab] = useState<'dialog' | 'beleg'>('dialog')

  const skipSapFetch = isAiDraft

  const { data: detail, isLoading, error } = useQuery({
    queryKey: ['sap-document-detail', doc?.docType, doc?.sapDocEntry, doc?.tenantId, refreshKey],
    queryFn: () => getDocumentDetail(doc!.docType, doc!.sapDocEntry, { tenantId: doc!.tenantId }),
    enabled: open && !!doc && !skipSapFetch,
    staleTime: doc?.isClosed ? 60 * 60 * 1000 : 5 * 60 * 1000,
  })

  if (!doc) return null

  const displayDoc = detail ?? doc
  const fullData = detail?.fullDataJson ?? null
  const sapFetchError = detail?._sapFetchError
  const cacheOutOfSync = detail?._cacheOutOfSync

  // Build a renderable doc object: prefer fullDataJson, fall back to
  // cached list fields + linesJson so SapDocumentView always has something.
  const renderDoc: Record<string, unknown> | null = fullData ?? (
    displayDoc.linesJson
      ? {
          DocNum: displayDoc.sapDocNum,
          DocDate: displayDoc.docDate,
          DocDueDate: displayDoc.docDueDate,
          CardCode: displayDoc.cardCode,
          CardName: displayDoc.cardName,
          DocTotal: displayDoc.docTotal,
          DocCurrency: displayDoc.docCurrency,
          DocumentStatus: displayDoc.docStatus,
          Comments: displayDoc.comments,
          DocumentLines: displayDoc.linesJson,
        }
      : null
  )
  const allowedTargets = ALLOWED_CONVERSIONS[doc.docType] ?? []
  const canConvert = !isAiDraft && isDocumentOpen(doc) && allowedTargets.length > 0

  const typeMap: Record<DocType, 'quotation' | 'order' | 'delivery' | 'invoice' | 'credit'> = {
    quotation: 'quotation',
    order: 'order',
    delivery: 'delivery',
    invoice: 'invoice',
    credit: 'credit',
    purchase_invoice: 'invoice',
    purchase_credit: 'credit',
    purchase_down_payment: 'invoice',
  }

  const titleNode = (
    <div className="flex items-center gap-4 min-w-0 flex-1">
      <span className="flex items-center gap-2 min-w-0">
        {isAi ? <Bot className="w-4 h-4 text-brand-600 flex-shrink-0" /> : <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />}
        <span className="truncate">
          {DOC_TYPE_LABELS[doc.docType]} {isAiDraft ? 'Entwurf' : formatDocNum(doc)}
          {doc.cardName && <span className="text-body text-gray-500 font-normal"> – {doc.cardName}</span>}
        </span>
      </span>
      {isAi && (
        <Tabs
          value={aiTab}
          onValueChange={(v) => setAiTab(v as 'dialog' | 'beleg')}
          className="flex-shrink-0"
        >
          <TabsList>
            <TabsTrigger value="dialog">
              <Bot className="w-3.5 h-3.5 mr-1.5" />
              KI-Verlauf
            </TabsTrigger>
            <TabsTrigger value="beleg" disabled={isAiDraft}>
              <FileText className="w-3.5 h-3.5 mr-1.5" />
              SAP-Beleg
              {isAiDraft && (
                <span className="ml-2 text-caption text-gray-400">(noch nicht exportiert)</span>
              )}
            </TabsTrigger>
          </TabsList>
        </Tabs>
      )}
      {/* Portal-Slot fuer die Aktionen aus AngebotDialogPage (Kunde aendern,
          Step-Debug, An SAP uebergeben). Wird im embedded-Modus aus der Dialog-
          Page via createPortal befuellt. */}
      {isAi && (
        <div
          id="quote-header-actions-slot"
          className="ml-auto flex items-center gap-2 flex-shrink-0"
        />
      )}
    </div>
  )

  async function handleReSyncTenant() {
    if (!cacheOutOfSync) return
    setIsSyncing(true)
    try {
      await triggerSync({ tenantId: cacheOutOfSync.tenantId })
      toast({ title: 'Sync gestartet', description: 'Cache wird neu synchronisiert…' })
      setRefreshKey((k) => k + 1)
    } catch {
      toast({ title: 'Sync fehlgeschlagen', description: 'Bitte erneut versuchen.', tone: 'danger' })
    } finally {
      setIsSyncing(false)
    }
  }

  // SAP error banner: structured out-of-sync card OR generic fetch error hint
  const sapErrorBanner = cacheOutOfSync ? (
    <div className="mb-3 rounded border border-orange-200 bg-orange-50 p-3">
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-orange-500" />
        <div className="flex-1 min-w-0">
          <p className="text-caption font-medium text-orange-800">Cache und SAP-Konfiguration nicht synchron</p>
          <p className="mt-0.5 text-caption text-orange-700">
            Dieses Dokument (DocEntry {cacheOutOfSync.docEntry}) war im Cache gespeichert, existiert aber
            {cacheOutOfSync.companyDb ? ` in der SAP-Datenbank „${cacheOutOfSync.companyDb}"` : ' in der konfigurierten SAP-Datenbank'} nicht mehr.
            Möglicherweise wurde es in SAP gelöscht oder die Mandanten-Konfiguration hat sich geändert.
          </p>
          <div className="mt-2 flex items-center gap-3">
            <Button
              variant="secondary"
              size="sm"
              onClick={handleReSyncTenant}
              disabled={isSyncing}
            >
              <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${isSyncing ? 'animate-spin' : ''}`} />
              {isSyncing ? 'Wird synchronisiert…' : 'Cache neu synchronisieren'}
            </Button>
            <button
              type="button"
              className="flex items-center gap-1 text-caption text-orange-600 hover:text-orange-800"
              onClick={() => setOutOfSyncDetailsOpen((v) => !v)}
            >
              {outOfSyncDetailsOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
              Details
            </button>
          </div>
          {outOfSyncDetailsOpen && (
            <pre className="mt-2 max-h-24 overflow-auto rounded bg-orange-100 p-2 text-caption text-orange-800 whitespace-pre-wrap break-all">
              {cacheOutOfSync.rawError}
            </pre>
          )}
        </div>
      </div>
    </div>
  ) : sapFetchError ? (
    <div className="mb-3 px-3 py-2 bg-yellow-50 border border-yellow-200 rounded text-caption text-yellow-800">
      SAP-Abruf fehlgeschlagen: {sapFetchError}
    </div>
  ) : null

  const errorInfo = error ? extractErrorMessage(error) : null

  // Action bar: ↻ + Umwandeln + optionale seiten-spezifische Aktionen
  const showActionBar = !isAiDraft || canConvert || !!actions
  const actionBar = showActionBar ? (
    <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100">
      {!isAiDraft && (
        <IconButton
          icon={<RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />}
          aria-label="Neu aus SAP laden"
          variant="ghost"
          onClick={() => {
            if (cacheOutOfSync) {
              void handleReSyncTenant()
              return
            }
            setRefreshKey((k) => k + 1)
          }}
          title="Neu aus SAP laden"
        />
      )}
      {canConvert && (
        <Dropdown>
          <DropdownTrigger asChild>
            <Button variant="primary" size="sm">
              Umwandeln ▾
            </Button>
          </DropdownTrigger>
          <DropdownContent align="start">
            {allowedTargets.map((target) => (
              <DropdownItem key={target} onSelect={() => onConvert(doc, target)}>
                In {DOC_TYPE_LABELS[target]} umwandeln
              </DropdownItem>
            ))}
          </DropdownContent>
        </Dropdown>
      )}
      {actions && <div className="flex items-center gap-2 ml-auto">{actions}</div>}
    </div>
  ) : null

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      size="full"
      title={titleNode}
      className="!w-[calc(100vw-2rem)] !max-w-none h-[90vh]"
    >
      <div className="flex flex-col flex-1 min-h-0 overflow-hidden h-full">
        {isAi ? (
          <Tabs
            value={aiTab}
            onValueChange={(v) => setAiTab(v as 'dialog' | 'beleg')}
            className="flex flex-col h-full min-h-0"
          >
            <TabsContent value="dialog" className="flex-1 min-h-0 overflow-hidden">
              {doc.localQuoteId && (
                <AngebotDialogPage
                  quoteId={doc.localQuoteId}
                  embedded
                  onClose={() => onOpenChange(false)}
                  onDeleted={onDeleted}
                />
              )}
            </TabsContent>

            <TabsContent value="beleg" className="flex-1 min-h-0 overflow-y-auto">
              <div className="px-1 py-3">
                {actionBar}
                {sapErrorBanner}
                {isLoading && <LoadingState label="Dokument wird geladen…" />}
                {errorInfo && (
                  <ErrorState
                    title="Fehler beim Laden"
                    description={errorInfo.description}
                    details={errorInfo.details}
                  />
                )}
                {!isLoading && !errorInfo && renderDoc && (
                  <SapDocumentView
                    doc={renderDoc}
                    type={typeMap[displayDoc.docType]}
                    showCustomerViewToggle={displayDoc.docType === 'quotation'}
                  />
                )}
                {!isLoading && !errorInfo && !renderDoc && !isAiDraft && (
                  <p className="text-caption text-gray-400">
                    Belegdaten konnten nicht aus SAP geladen werden. Bitte ↻ erneut versuchen.
                  </p>
                )}
              </div>
            </TabsContent>
          </Tabs>
        ) : (
          <div className="flex-1 min-h-0 overflow-y-auto">
            {actionBar}
            {sapErrorBanner}
            {isLoading && <LoadingState label="Dokument wird geladen…" />}
            {errorInfo && (
              <ErrorState
                title="Fehler beim Laden"
                description={errorInfo.description}
                details={errorInfo.details}
              />
            )}
            {!isLoading && !errorInfo && (
              renderDoc
                ? <SapDocumentView
                    doc={renderDoc}
                    type={typeMap[displayDoc.docType]}
                    showCustomerViewToggle={displayDoc.docType === 'quotation'}
                  />
                : (
                  <p className="text-caption text-gray-400 py-4">
                    Belegdaten konnten nicht aus SAP geladen werden. Bitte ↻ erneut versuchen.
                  </p>
                )
            )}
          </div>
        )}
      </div>
    </Modal>
  )
}
